import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { mdlReservation } from "../../models/domain/Reservation";
import dayjs from "dayjs";
import { Logo } from "../common/Logo";
import _ from "lodash";
import { enmQuotaType } from "../../models/enums/QuotaType";
import { enmReservationStatus } from "../../models/enums/ReservationStatus";
import { InputHelper } from "../../utils/InputHelper";
import { enmReservationType } from "../../models/enums/ReservationType";

type ReservationListTemplateProps = {
    reservations?: mdlReservation[];
}

const ReservationListTemplate = (props: ReservationListTemplateProps) => {
    const { t } = useTranslation();

    // Önce acentelere göre grupla
    const groupedByAgency = _.groupBy(props.reservations, 'agencyName');
    console.log(groupedByAgency);

    const getQuotaType = (quotaType: enmQuotaType) => {
        switch (quotaType) {
            case enmQuotaType.Guarantee:
                return t("pdf.listEntry.quotaType.guarantee");
            case enmQuotaType.Normal:
                return t("pdf.listEntry.quotaType.normal");
            case enmQuotaType.OnRequest:
                return t("pdf.listEntry.quotaType.onRequest");
            case enmQuotaType.Mix:
                return t("pdf.listEntry.quotaType.mix");
        }
    }

    const getResTypeText = (reservationType?: enmReservationType) => {
        if (reservationType != null) {
            var resTypeText = enmReservationType[reservationType];
            return t(`reservationDetail.resType${InputHelper.capitalizeFirstLetter(resTypeText.toLowerCase())}`)
        }
        return "-";
    }

    return (
        <div className="container-fluid">
            {Object.entries(groupedByAgency).map(([agencyName, agencyReservations], agencyIndex) => {
                // Önce rezervasyonları check-in tarihine göre sırala
                const sortedReservations = _.sortBy(agencyReservations, 'checkIn');
                const groupedByHotel = _.groupBy(sortedReservations, 'hotelName');
                const firstRes = _.first(agencyReservations);
                return (
                    <div key={agencyName} style={{
                        pageBreakBefore: agencyIndex > 0 ? 'always' : 'auto',
                        pageBreakAfter: 'always',
                        pageBreakInside: 'avoid',
                        padding: '10pt',
                        width: '100%'
                    }} className={`${agencyIndex > 0 ? 'page-break' : ''}`}>
                        <div className="row">
                            <div className="col-sm-12 mb-2">
                                <div className="row">
                                    <div className="col-8">
                                        <Logo path={firstRes?.agencyId} />
                                        <h2 className="mt-2" style={{ fontSize: '14pt', fontWeight: 'bold' }}>{agencyName}</h2>
                                    </div>
                                    <div className="col-4 text-end">
                                        <div style={{ fontSize: '8pt' }}>Print out Date: {dayjs().format("DD.MM.YYYY")}</div>
                                        <div style={{ fontSize: '8pt' }}>Time: {dayjs().format("HH:mm")}</div>
                                    </div>
                                </div>
                            </div>

                            {Object.entries(groupedByHotel).map(([hotelName, hotelReservations], hotelIndex) => (
                                <div key={hotelName} className="col-sm-12" style={{                                    
                                    marginBottom: '10pt'
                                }}>
                                    <h3 className="mb-2 text-center" style={{ fontSize: '12pt', fontWeight: 'bold' }}>{hotelName}</h3>
                                    <table className="table table-bordered table-sm" style={{ fontSize: '7pt' }}>
                                        <thead>
                                            <tr>
                                                <th className="fw-bold text-dark p-1">{t("pdf.listEntry.operator")}</th>
                                                <th className="fw-bold text-dark p-1">{t("pdf.listEntry.voucher")}</th>
                                                <th className="fw-bold text-dark p-1">{t("pdf.listEntry.accom")}</th>
                                                <th className="fw-bold text-dark p-1">{t("pdf.listEntry.day")}</th>
                                                <th className="fw-bold text-dark p-1">{t("pdf.listEntry.roomCount")}</th>
                                                <th className="fw-bold text-dark p-1">{t("pdf.listEntry.room")}</th>
                                                <th className="fw-bold text-dark p-1">{t("pdf.listEntry.roomType")}</th>
                                                <th className="fw-bold text-dark p-1">{t("pdf.listEntry.accomType")}</th>
                                                <th className="fw-bold text-dark p-1">{t("pdf.listEntry.status")}</th>
                                                <th className="fw-bold text-dark p-1">{t("pdf.listEntry.sellDate")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {hotelReservations.map((res, idx) => (
                                                <Fragment key={idx}>
                                                    <tr className="text-dark">
                                                        <td className="fw-medium p-1">{res.operator}</td>
                                                        <td className="fw-medium p-1">{res.voucherNo}</td>
                                                        <td className="fw-medium p-1">{dayjs(res.checkIn).format("DD.MM.YYYY")} - {dayjs(res.checkOut).format("DD.MM.YYYY")}</td>
                                                        <td className="fw-medium p-1">{res.nights}</td>
                                                        <td className="fw-medium p-1">{res.roomCount}</td>
                                                        <td className="fw-medium p-1">{res.roomName}</td>
                                                        <td className="fw-medium p-1">{res.roomTypeName}</td>
                                                        <td className="fw-medium p-1">{res.accomCode}</td>
                                                        <td className="fw-medium p-1">{getResTypeText(res.reservationType)}</td>
                                                        <td className="fw-medium p-1">{dayjs(res.sellDate).format("DD.MM.YYYY")}</td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={10}>
                                                            <div className="ms-2 mt-1 mb-1">
                                                                <strong style={{ fontSize: '7pt' }}>{t("pdf.listEntry.customerInfo")}:</strong>
                                                                <table className="table table-sm table-bordered mt-1 customer-table" style={{ fontSize: '7pt' }}>
                                                                    <thead>
                                                                        <tr className="bg-light">
                                                                            <th className="fw-bold text-dark p-1">{t("pdf.listEntry.customerName")}</th>
                                                                            <th className="fw-bold text-dark p-1">{t("pdf.listEntry.age")}</th>
                                                                            <th className="fw-bold text-dark p-1">{t("pdf.listEntry.birthDate")}</th>
                                                                            <th className="fw-bold text-dark p-1">{t("pdf.listEntry.nationality")}</th>
                                                                            <th className="fw-bold text-dark p-1">{t("pdf.listEntry.arrival")}</th>
                                                                            <th className="fw-bold text-dark p-1">{t("pdf.listEntry.arrivalTime")}</th>
                                                                            <th className="fw-bold text-dark p-1">{t("pdf.listEntry.departure")}</th>
                                                                            <th className="fw-bold text-dark p-1">{t("pdf.listEntry.departureTime")}</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {res.customers?.map((customer, customerIdx) => (
                                                                            <tr key={customerIdx} className="text-dark">
                                                                                <td className="fw-medium p-1">{customer.name}</td>
                                                                                <td className="fw-medium p-1">{customer.age ?? "-"}</td>
                                                                                <td className="fw-medium p-1">{customer.birthDateString != "-" ? dayjs(customer.birthDate).format("DD.MM.YYYY") : '-'}</td>
                                                                                <td className="fw-medium p-1">{customer.nationality}</td>
                                                                                <td className="fw-medium p-1">{customer.arrival}</td>
                                                                                <td className="fw-medium p-1">{customer.arrivalTimeString ?? "-"}</td>
                                                                                <td className="fw-medium p-1">{customer.departure}</td>
                                                                                <td className="fw-medium p-1">{customer.departureTimeString ?? "-"}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                            <div className="ms-2 mt-1" style={{ fontSize: '7pt' }}>
                                                                {res.note && (
                                                                    <span className="me-3">
                                                                        <span className="fw-bold text-dark">{t("pdf.listEntry.note")}: </span>
                                                                        <span className="fw-medium">{res.note}</span>
                                                                    </span>
                                                                )}
                                                                {res.quotaType && (
                                                                    <span>
                                                                        <span className="fw-bold text-dark">{t("pdf.listEntry.quotaTypeTitle")}: </span>
                                                                        <span className="fw-medium">{getQuotaType(res.quotaType)}</span>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </Fragment>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-light">
                                                <td colSpan={10} className="fw-bold text-dark p-1" style={{ fontSize: '8pt' }}>
                                                    <strong>Otel Toplam Pax: {_.sumBy(hotelReservations, r => (r.adults || 0) + (r.childs || 0) + (r.infants || 0))}</strong>
                                                    <br />
                                                    <strong>Otel Toplam Oda: {_.sumBy(hotelReservations, r => r.roomCount || 0)}</strong>
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>

                                </div>
                            ))}

                            <div className="col-sm-12 mb-2">
                                <div className="card">
                                    <div className="card-body bg-light p-2">
                                        <strong style={{ fontSize: '8pt' }}>Acente Genel Toplam:</strong>
                                        <br />
                                        <strong style={{ fontSize: '8pt' }}>Toplam Pax: {_.sumBy(agencyReservations, r => (r.adults || 0) + (r.childs || 0) + (r.infants || 0))}</strong>
                                        <br />
                                        <strong style={{ fontSize: '8pt' }}>Toplam Oda: {_.sumBy(agencyReservations, r => r.roomCount || 0)}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default ReservationListTemplate; 